import axios from "axios";
import { showToast } from "../store/actions";
import store from "../store";

const axiosInstance = axios.create({
  baseURL: (process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL : "") + "/api/v1",
  withCredentials: true,
});

const handleError = (error) => {
  //API call got canceled
  if (axios.isCancel(error)) return { success: false, message: "API canceled!" };

  if (error.response.status.toString().startsWith("5")) {
    if (error.response.status === 500)
      store.dispatch(showToast({ message: "Internal error occurred for requested operation!", type: "error" }));
    else if (error.response.status === 502 || error.response.status === 503)
      store.dispatch(showToast({ message: "The server is temporarily busy, try again later!", type: "error" }));
    else if (error.response.status === 504)
      store.dispatch(showToast({ message: "Time out for requested operation!", type: "error" }));
    else store.dispatch(showToast({ message: "Server error for requested operation!", type: "error" }));
  } else if (error.response.status !== 404) {
    const err = error.response.data;

    store.dispatch(
      showToast({ message: err.errors ? err.errors.join("\r\n") : "Something went wrong", type: "error" })
    );

    //reloading page when user does not have permission to perform some action
    if (error.response.status === 403) window.location.reload();
  }

  return error.response;
};

export default class FetchApi {
  createController() {
    this.controller = new AbortController();
    return this.controller.signal;
  }

  cancelRequest() {
    // Abort the current request
    if (this.controller) {
      this.controller.abort();
      console.log("API request canceled");
      this.controller = null;
    }
  }

  get(url, onSuccess, onError) {
    const signal = this.createController();
    return axiosInstance
      .get(url, {
        signal,
      })
      .then(onSuccess)
      .catch((error) => {
        if (typeof onError === "function") {
          const { onErrorReturn, errorResponse } = onError(error) || {};
          if (onErrorReturn) return errorResponse;
        }
        return handleError(error);
      });
  }

  post(url, data, onSuccess, onError = undefined) {
    const signal = this.createController();
    return axiosInstance
      .post(url, data, {
        signal,
      })
      .then(onSuccess)
      .catch((error) => {
        if (typeof onError === "function") {
          const { onErrorReturn, errorResponse } = onError(error) || {};
          if (onErrorReturn) return errorResponse;
        }
        return handleError(error);
      });
  }

  patch(url, data, onSuccess) {
    const signal = this.createController();
    return axiosInstance
      .patch(url, data, {
        signal,
      })
      .then(onSuccess)
      .catch(handleError);
  }

  delete(url, onSuccess) {
    const signal = this.createController();
    return axiosInstance
      .delete(url, {
        signal,
      })
      .then(onSuccess)
      .catch(handleError);
  }

  download(url, { fileName }, onSuccess) {
    const signal = this.createController();
    return axiosInstance
      .get(url, {
        signal,
        responseType: "blob",
      })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName); //or any other extension
        document.body.appendChild(link);
        link.click();
        onSuccess && onSuccess();
      })
      .catch(handleError);
  }

  // Use When you intend to transmit PostData and retrieve result in download
  downloadPost(url, data, fileName, onSuccess) {
    const signal = this.createController();
    return axiosInstance
      .post(url, data, {
        signal,
        responseType: "blob",
      })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName); //or any other extension
        document.body.appendChild(link);
        link.click();
        onSuccess && onSuccess();
      })
      .catch(handleError);
  }

  put(url, data, customHeaders = {}, customOptions = {}, onSuccess = null) {
    const signal = this.createController();
    return axios
      .put(url, data, {
        signal,
        headers: { ...customHeaders },
        ...(customOptions.onUploadProgress ? { onUploadProgress: customOptions.onUploadProgress } : {}),
      })
      .then(onSuccess)
      .catch((error) => {
        return error.response;
      });
  }

  postFormData(url, formData, customHeaders = {}, customOptions = {}, onSuccess) {
    const signal = this.createController();
    return axiosInstance
      .post(url, formData, {
        headers: {
          "Content-type": "multipart/form-data",
          ...customHeaders,
        },
        signal,
        ...(customOptions.onUploadProgress ? { onUploadProgress: customOptions.onUploadProgress } : {}),
      })
      .then(onSuccess)
      .catch(handleError);
  }
}
